import { fireQuickInformationToast } from '../toast-away';
import { headerServiceRestarting } from './network-consts';
import { LangToastAway } from '../../../localization/webmodule-common/other/toast-away-lang';

/**
 * Configuration for service restart retry behavior
 */
export interface ServiceRestartConfig {
  /** Initial delay in milliseconds before first retry (default: 2000) */
  initialDelayMs?: number;
  /** Maximum delay in milliseconds between retries (default: 30000) */
  maxDelayMs?: number;
  /** Maximum number of retry attempts (default: 5) */
  maxRetries?: number;
  /** Whether to show toast notification to user (default: true) */
  showNotification?: boolean;
}

const defaultConfig: Required<ServiceRestartConfig> = {
  initialDelayMs: 2000,
  maxDelayMs: 30000,
  maxRetries: 5,
  showNotification: true
};

let activeRestartNotification = false;

/**
 * Checks if a response indicates a planned service restart
 * @param response The fetch Response object
 * @returns true if the response indicates a planned service restart
 */
export function isServiceRestarting(response: Response | null): boolean {
  if (!response) return false;
  return response.status === 503 && response.headers.get(headerServiceRestarting) === 'true';
}

/**
 * Shows a non-intrusive notification that the service is updating.
 * Only shows one notification at a time to avoid spam.
 */
export function showServiceRestartingNotification(): void {
  if (activeRestartNotification) return;

  activeRestartNotification = true;
  fireQuickInformationToast(LangToastAway.serviceRestartingMessage(), 5000);

  // Reset the flag after the toast would have auto-hidden
  setTimeout(() => {
    activeRestartNotification = false;
  }, 5500);
}

/**
 * Calculates the delay for the next retry using exponential backoff
 * @param attemptNumber Current attempt number (0-based)
 * @param config Configuration for retry behavior
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(attemptNumber: number, config: Required<ServiceRestartConfig>): number {
  // Exponential backoff: 2s, 4s, 8s, 16s, capped at maxDelayMs
  const delay = config.initialDelayMs * Math.pow(2, attemptNumber);
  return Math.min(delay, config.maxDelayMs);
}

/**
 * Options for the retry fetch operation
 */
export interface RetryFetchOptions {
  url: string;
  fetchOptions: RequestInit;
  config?: ServiceRestartConfig;
}

/**
 * Retries a fetch operation when the service is restarting, using exponential backoff.
 * Shows a user-friendly notification and transparently retries until success or max retries.
 *
 * @param options The retry fetch options
 * @returns The successful response, or null if all retries are exhausted
 */
export async function retryFetchOnServiceRestart(options: RetryFetchOptions): Promise<Response | null> {
  const config: Required<ServiceRestartConfig> = { ...defaultConfig, ...options.config };

  for (let attempt = 0; attempt < config.maxRetries; attempt++) {
    // Show notification on first attempt
    if (attempt === 0 && config.showNotification) {
      showServiceRestartingNotification();
    }

    const delay = calculateBackoffDelay(attempt, config);
    await new Promise(resolve => setTimeout(resolve, delay));

    try {
      const response = await fetch(options.url, options.fetchOptions);

      // If service is still restarting, continue retrying
      if (isServiceRestarting(response)) {
        console.log(`Service still restarting, retry ${attempt + 1}/${config.maxRetries}`);
        continue;
      }

      // Service is back, return the response
      return response;
    } catch (error) {
      console.error(`Retry attempt ${attempt + 1} failed:`, error);
      // Network error during restart, continue retrying
      continue;
    }
  }

  // All retries exhausted
  console.error(`Service restart: all ${config.maxRetries} retries exhausted`);
  return null;
}
